import { useState } from 'react';
import { Tabs, Input, Button, Select, Space, Popconfirm, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import MDEditor from '@uiw/react-md-editor';

const { Text } = Typography;

const KNOWN_LOCALES = [
  { value: 'de', label: 'Deutsch (de)' },
  { value: 'en', label: 'English (en)' },
  { value: 'fr', label: 'Français (fr)' },
  { value: 'es', label: 'Español (es)' },
  { value: 'it', label: 'Italiano (it)' },
  { value: 'nl', label: 'Nederlands (nl)' },
  { value: 'pl', label: 'Polski (pl)' },
];

function LocaleTab({ t, onUpdate, onRemove }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>Name</Text>
        <Input
          style={{ marginTop: 4 }}
          value={t.name || ''}
          onChange={e => onUpdate('name', e.target.value)}
          placeholder="Name in dieser Sprache"
        />
      </div>
      <div data-color-mode="light">
        <Text type="secondary" style={{ fontSize: 12 }}>Kurzbeschreibung</Text>
        <div style={{ marginTop: 4 }}>
          <MDEditor
            value={t.short_description || ''}
            onChange={v => onUpdate('short_description', v || '')}
            height={120}
            preview="edit"
          />
        </div>
      </div>
      <div data-color-mode="light">
        <Text type="secondary" style={{ fontSize: 12 }}>Beschreibungstext</Text>
        <div style={{ marginTop: 4 }}>
          <MDEditor
            value={t.description || ''}
            onChange={v => onUpdate('description', v || '')}
            height={220}
            preview="edit"
          />
        </div>
      </div>
      <div style={{ textAlign: 'right', paddingTop: 4 }}>
        <Popconfirm
          title={`Sprache "${t.locale}" entfernen?`}
          onConfirm={onRemove}
          okText="Ja"
          cancelText="Nein"
        >
          <Button danger size="small" icon={<DeleteOutlined />}>Sprache entfernen</Button>
        </Popconfirm>
      </div>
    </div>
  );
}

export default function TranslationsEditor({ translations = [], onChange, defaultName = '' }) {
  const [pendingLocale, setPendingLocale] = useState(null);

  function update(locale, field, value) {
    onChange(translations.map(t => t.locale === locale ? { ...t, [field]: value } : t));
  }

  function addLocale() {
    if (!pendingLocale || translations.some(t => t.locale === pendingLocale)) return;
    onChange([...translations, { locale: pendingLocale, name: defaultName, short_description: '', description: '' }]);
    setPendingLocale(null);
  }

  function removeLocale(locale) {
    onChange(translations.filter(t => t.locale !== locale));
  }

  const usedLocales = new Set(translations.map(t => t.locale));
  const availableLocales = KNOWN_LOCALES.filter(l => !usedLocales.has(l.value));

  const tabItems = translations.map(t => ({
    key: t.locale,
    label: KNOWN_LOCALES.find(l => l.value === t.locale)?.label.split(' ')[0] || t.locale,
    children: (
      <LocaleTab
        t={t}
        onUpdate={(field, value) => update(t.locale, field, value)}
        onRemove={() => removeLocale(t.locale)}
      />
    ),
  }));

  return (
    <div>
      {translations.length === 0 && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Noch keine Übersetzungen vorhanden.
        </Text>
      )}
      {tabItems.length > 0 && <Tabs items={tabItems} style={{ marginBottom: 12 }} />}
      <Space>
        <Select
          placeholder="Sprache hinzufügen…"
          value={pendingLocale}
          onChange={setPendingLocale}
          options={availableLocales}
          style={{ width: 200 }}
          allowClear
        />
        <Button
          icon={<PlusOutlined />}
          onClick={addLocale}
          disabled={!pendingLocale}
        >
          Hinzufügen
        </Button>
      </Space>
    </div>
  );
}
